/**
 * SMS Notification Settings
 *
 * The A2P 10DLC opt-in surface. Its exact wording and behavior are described in
 * the campaign registration submitted to The Campaign Registry, and a reviewer
 * may ask for a screenshot of it, so the following are compliance requirements
 * rather than design preferences:
 *
 *  1. The phone field is OPTIONAL and visibly labelled so. A required phone
 *     field without an SMS disclaimer is itself a rejection reason.
 *  2. The consent checkbox is UNCHECKED by default and is its own separate
 *     agreement — never bundled with terms acceptance or anything else.
 *  3. The disclosure text is shown in full next to the checkbox, and is the
 *     same string recorded against the user (see config/legal.ts).
 *  4. Consent is NOT a condition of using the Service, and the copy says so.
 *  5. Opting out is one click, always available, never gated.
 *
 * Changing the disclosure wording means bumping SMS_CONSENT_VERSION in
 * config/legal.ts AND the matching constant in the backend controller.
 */

import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  getSmsConsentStatus,
  startSmsConsent,
  confirmSmsConsent,
  optOutOfSms,
  type SmsConsentStatus,
} from '../../api/smsConsent';
import { getErrorMessage } from '../../api/client';
import { SMS_CONSENT_TEXT } from '../../config/legal';

type Stage = 'loading' | 'idle' | 'awaiting-code';

export default function SmsNotificationSettings() {
  const [status, setStatus] = useState<SmsConsentStatus | null>(null);
  const [stage, setStage] = useState<Stage>('loading');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devMode, setDevMode] = useState(false);
  const location = useLocation();

  // The dashboard prompt links to /crew/profile#sms-notifications. React Router
  // does not scroll to hash targets, and this section is below the fold, so
  // without this the nudge appears to do nothing.
  useEffect(() => {
    if (location.hash !== '#sms-notifications') return;
    // Wait for the loading skeleton to be replaced by real content, or we scroll
    // to an element that is about to change height.
    if (stage === 'loading') return;
    document.getElementById('sms-notifications')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash, stage]);

  useEffect(() => {
    let cancelled = false;
    getSmsConsentStatus()
      .then((s) => {
        if (cancelled) return;
        setStatus(s);
        setStage(s.awaitingVerification ? 'awaiting-code' : 'idle');
      })
      .catch(() => {
        if (!cancelled) setStage('idle');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function refresh() {
    const s = await getSmsConsentStatus();
    setStatus(s);
    return s;
  }

  async function handleStart(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Guard in the UI as well as the server. Submitting an unchecked box is the
    // precise pattern carriers reject, so it must not be possible from here.
    if (!consent) {
      setError('Please check the box to agree to receive text messages.');
      return;
    }

    setBusy(true);
    try {
      const res = await startSmsConsent(phone, consent);
      setDevMode(res.devMode);
      setStage('awaiting-code');
      setCode('');
      await refresh();
      toast.success(res.message);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await confirmSmsConsent(code);
      setStage('idle');
      setConsent(false);
      setPhone('');
      await refresh();
      toast.success(res.message);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleOptOut() {
    setError(null);
    setBusy(true);
    try {
      const res = await optOutOfSms();
      setStage('idle');
      await refresh();
      toast.success(res.message);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function handleChangeNumber() {
    setStage('idle');
    setPhone('');
    setConsent(false);
    setCode('');
    setError(null);
  }

  if (stage === 'loading') {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Text Message Notifications</h2>
        <div className="h-20 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  const isOn = status?.enabled ?? false;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" id="sms-notifications">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Text Message Notifications</h2>
          <p className="text-sm text-gray-500 mt-1">
            Get a text when you're assigned to an event or a call time changes. Optional; email
            notifications work either way.
          </p>
        </div>
        {isOn && (
          <span className="shrink-0 inline-flex items-center px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-800">
            On
          </span>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Already on — show the number and a one-click way off.            */}
      {/* ---------------------------------------------------------------- */}
      {isOn && (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
            <div>
              <p className="text-sm font-medium text-gray-900">{status?.maskedPhone}</p>
              <p className="text-xs text-gray-500">Verified</p>
            </div>
            <button
              type="button"
              onClick={handleChangeNumber}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Change number
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Reply <strong>STOP</strong> to any message to turn texts off, or{' '}
            <strong>HELP</strong> for help. Message frequency varies. Message and data rates may
            apply.
          </p>
          <button
            type="button"
            onClick={handleOptOut}
            disabled={busy}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {busy ? 'Turning off…' : 'Turn off text messages'}
          </button>
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Awaiting the one-time passcode.                                   */}
      {/* ---------------------------------------------------------------- */}
      {!isOn && stage === 'awaiting-code' && (
        <form onSubmit={handleConfirm} className="space-y-4">
          <p className="text-sm text-gray-700">
            Enter the 6-digit code we texted to{' '}
            <strong>{status?.maskedPhone ?? 'your phone'}</strong>.
          </p>
          {devMode && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Development mode. No text was sent. Use code <strong>000000</strong>.
            </p>
          )}
          <div>
            <label htmlFor="sms-code" className="block text-sm font-medium text-gray-700 mb-1">
              Verification code
            </label>
            <input
              id="sms-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              className="w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm tracking-widest font-mono focus:ring-rack focus:border-rack"
              required
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={busy || code.length < 6}
              className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {busy ? 'Verifying…' : 'Verify and turn on'}
            </button>
            <button
              type="button"
              onClick={handleChangeNumber}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
            >
              Use a different number
            </button>
          </div>
        </form>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Off — the opt-in form. This is the screen a TCR reviewer sees.    */}
      {/* ---------------------------------------------------------------- */}
      {!isOn && stage === 'idle' && (
        <form onSubmit={handleStart} className="space-y-4">
          {status?.optedOut && (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
              Text messages are currently off. You can turn them back on below.
            </p>
          )}

          {status?.hasUnverifiedContactPhone && !status?.optedOut && (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
              Your organization has a phone number on file for you as contact information. We
              won't text it until you add and verify a number here yourself.
            </p>
          )}

          <div>
            <label htmlFor="sms-phone" className="block text-sm font-medium text-gray-700 mb-1">
              Mobile number <span className="font-normal text-gray-500">(optional)</span>
            </label>
            <input
              id="sms-phone"
              type="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(412) 555-0143"
              className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-rack focus:border-rack"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              US or Canadian mobile numbers only. We'll text you a code to confirm it's yours.
            </p>
          </div>

          {/* The consent checkbox. Unchecked by default, standalone, with the
              full disclosure visible — not behind a tooltip or a link. */}
          <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
            <input
              id="sms-consent"
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 border-gray-300 rounded focus:ring-rack"
            />
            <label htmlFor="sms-consent" className="text-sm text-gray-700">
              I agree to receive text messages from ProgramRow about my crew assignments and
              schedule changes. Message frequency varies. Message and data rates may apply. Reply
              STOP to opt out or HELP for help. See our{' '}
              <Link to="/privacy" target="_blank" className="text-primary-600 hover:underline">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link to="/terms" target="_blank" className="text-primary-600 hover:underline">
                Terms of Service
              </Link>
              .
            </label>
          </div>

          <p className="text-xs text-gray-500">
            Consent is not required to use ProgramRow, and turning texts off never affects your
            account or your assignments.
          </p>

          <button
            type="submit"
            disabled={busy || !consent || !phone.trim()}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? 'Sending code…' : 'Send verification code'}
          </button>
        </form>
      )}
    </div>
  );
}

/**
 * Exported so the consent record and the rendered copy cannot drift: a test
 * asserts the label above matches SMS_CONSENT_TEXT word for word.
 */
export const RENDERED_CONSENT_TEXT = SMS_CONSENT_TEXT;
